/**
 * Weld map + pressure test package for the Pipe Sketch tool.
 *
 * Every joint the sketch implies (fitting welds, valve / flange joints, cap and
 * nozzle joints) is numbered into a weld map the way a fab shop would: W-01, W-02 …
 * Each weld carries its size, joint type and shop / field position, which rolls up
 * into diameter-inch (DI) totals — the standard unit for welding productivity and
 * NDE scope.
 *
 * Test pressures follow ASME B31.3:
 *  - Hydrostatic: 1.5 x design pressure (para. 345.4.2)
 *  - Pneumatic:   1.1 x design pressure (para. 345.5.4)
 *  - Gauge range: 1.5 to 4 x test pressure (para. 345.2.1)
 *
 * Everything here is deterministic and offline — plain tables and arithmetic.
 */

import { getMaterial, type MaterialId } from "@/lib/pipe-spec";

export type WeldPosition = "shop" | "field";

export interface WeldItem {
  /** stable key derived from geometry so overrides survive edits */
  id: string;
  /** sequential mark, e.g. "W-01" */
  no: string;
  runId: string;
  /** vertex or segment index the joint sits at */
  index: number;
  size: string;
  /** nominal size in inches, for diameter-inch math */
  nom: number;
  /** joint description, e.g. "Butt weld (BW)" */
  jointLabel: string;
  /** what the pipe joins to, e.g. `2" 90° Elbow LR BW` */
  connects: string;
  position: WeldPosition;
  /** diameter inches for this joint */
  di: number;
}

export interface WeldMapInput {
  material: MaterialId;
  fittings: Array<{ runId: string; index: number; size: string; label: string; welds: number }>;
  placements: Array<{ runId: string; index: number; size: string; label: string; joints: number }>;
  terminations: Array<{ runId: string; size: string; label: string; joints: number }>;
  /** weld id -> shop/field override */
  positions?: Record<string, WeldPosition>;
  /** when true, every fitting-to-pipe joint defaults to a shop weld */
  shopFabDefault?: boolean;
}

const nomOf = (nps: string) => {
  const m = nps.match(/(\d+)\s*-?\s*(\d+)\/(\d+)/);
  if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
  const f = nps.match(/(\d+)\/(\d+)/);
  if (f) return Number(f[1]) / Number(f[2]);
  return parseFloat(nps) || 1;
};

export function jointLabelFor(material: MaterialId): string {
  const t = getMaterial(material).jointType;
  return t === "bw" ? "Butt weld (BW)" : t === "solder" ? "Solder joint" : "Solvent weld";
}

/** Does this material actually get welded (and therefore NDE'd)? */
export function isWelded(material: MaterialId): boolean {
  return getMaterial(material).jointType === "bw";
}

/** Build the numbered weld map from the derived sketch geometry. */
export function buildWeldMap(input: WeldMapInput): WeldItem[] {
  const { material, fittings, placements, terminations, positions = {}, shopFabDefault } = input;
  const jointLabel = jointLabelFor(material);
  const out: WeldItem[] = [];

  const push = (
    runId: string,
    index: number,
    size: string,
    connects: string,
    seq: number,
    defaultShop: boolean,
  ) => {
    const id = `${runId}|${index}|${connects}|${seq}`;
    const nom = nomOf(size);
    out.push({
      id,
      no: "",
      runId,
      index,
      size,
      nom,
      jointLabel,
      connects,
      position: positions[id] ?? (defaultShop ? "shop" : "field"),
      di: nom,
    });
  };

  const ordered = [...fittings].sort((a, b) =>
    a.runId === b.runId ? a.index - b.index : a.runId < b.runId ? -1 : 1,
  );
  for (const f of ordered) {
    for (let i = 0; i < f.welds; i++) {
      push(f.runId, f.index, f.size, f.label, i, !!shopFabDefault);
    }
  }
  for (const p of placements) {
    for (let i = 0; i < p.joints; i++) {
      push(p.runId, p.index, p.size, p.label, i, false);
    }
  }
  for (const t of terminations) {
    for (let i = 0; i < t.joints; i++) {
      push(t.runId, 0, t.size, t.label, i, false);
    }
  }

  return out.map((w, i) => ({ ...w, no: `W-${String(i + 1).padStart(2, "0")}` }));
}

export interface WeldTotals {
  count: number;
  shop: number;
  field: number;
  di: number;
  shopDi: number;
  fieldDi: number;
}

export function weldTotals(welds: WeldItem[]): WeldTotals {
  let shop = 0;
  let field = 0;
  let di = 0;
  let shopDi = 0;
  let fieldDi = 0;
  for (const w of welds) {
    di += w.di;
    if (w.position === "shop") {
      shop += 1;
      shopDi += w.di;
    } else {
      field += 1;
      fieldDi += w.di;
    }
  }
  return { count: welds.length, shop, field, di, shopDi, fieldDi };
}

/* ------------------------------------------------------------------ */
/* Pressure test package                                               */
/* ------------------------------------------------------------------ */

export type TestMedium = "hydro" | "pneumatic" | "service";

export const TEST_MEDIA: Array<{ id: TestMedium; label: string; note: string }> = [
  {
    id: "hydro",
    label: "Hydrostatic (water)",
    note: "ASME B31.3 345.4.2 — 1.5 x design pressure, hold 10 min minimum",
  },
  {
    id: "pneumatic",
    label: "Pneumatic (air / nitrogen)",
    note: "ASME B31.3 345.5.4 — 1.1 x design pressure, step up in stages",
  },
  {
    id: "service",
    label: "Initial service leak test",
    note: "ASME B31.3 345.7 — at operating pressure, Category D fluid only",
  },
];

export const NDE_LEVELS = [0, 5, 10, 20, 100] as const;
export type NdePercent = (typeof NDE_LEVELS)[number];

export interface TestPackage {
  /** design pressure, psig */
  designPressure: number;
  medium: TestMedium;
  /** hold time, minutes */
  holdMinutes: number;
  /** radiography / NDE scope, percent of welds */
  ndePercent: NdePercent;
  system: string;
  testedBy: string;
  witnessedBy: string;
}

export const DEFAULT_TEST_PACKAGE: TestPackage = {
  designPressure: 150,
  medium: "hydro",
  holdMinutes: 30,
  ndePercent: 5,
  system: "",
  testedBy: "",
  witnessedBy: "",
};

export function testMultiplier(medium: TestMedium): number {
  return medium === "hydro" ? 1.5 : medium === "pneumatic" ? 1.1 : 1;
}

export interface TestResult {
  testPressure: number;
  multiplier: number;
  /** recommended gauge full-scale range, psi */
  gaugeMin: number;
  gaugeMax: number;
  note: string;
  /** welds to be examined at the chosen NDE scope */
  ndeWelds: number;
  ndeDi: number;
}

export function evaluateTest(pkg: TestPackage, welds: WeldItem[]): TestResult {
  const multiplier = testMultiplier(pkg.medium);
  const testPressure = Math.round(pkg.designPressure * multiplier);
  const meta = TEST_MEDIA.find((m) => m.id === pkg.medium)!;
  const ndeWelds = Math.ceil((welds.length * pkg.ndePercent) / 100);
  // examine the largest joints first — that is how a scope is normally picked
  const sorted = [...welds].sort((a, b) => b.di - a.di).slice(0, ndeWelds);
  return {
    testPressure,
    multiplier,
    gaugeMin: Math.round(testPressure * 1.5),
    gaugeMax: Math.round(testPressure * 4),
    note: meta.note,
    ndeWelds,
    ndeDi: sorted.reduce((s, w) => s + w.di, 0),
  };
}
